import { Button, Tag, TaskCard } from "@wayflyer/flyui";
import { FundingApplicationSchemas } from "api-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { getAirCta, getAirDescription, getAirTimeEstimate, getAirTitle, getAirTitleKey } from "./card-content";
import { getCompletedAirResources } from "./completed-resources";
import { getManualAirDescription, getManualAirTitle, getManualAirTitleKey } from "./manual-air-card-content";
import { ManualAirDetailsPanel } from "./manual-air-details-panel";
import { MANUAL_AIR_MESSAGES } from "./manual-air-message-definitions";
import { MESSAGES } from "./message-definitions";

export interface AIRListNextProps {
  airsData: FundingApplicationSchemas.AdditionalInformationRequestsData;
  airPath: string;
}

export const AIRListNext = ({ airsData, airPath }: AIRListNextProps) => {
  const intl = useIntl();
  const { navigate } = useSpaNavigate("FundingApp");
  const { airs, manualAirs, hasActiveManualAirs } = airsData;
  const [selectedManualAir, setSelectedManualAir] =
    useState<FundingApplicationSchemas.ManualAdditionalInformationRequestSchema | null>(null);

  if (airs.length === 0 && !hasActiveManualAirs) {
    return null;
  }

  const activeManualAirs = manualAirs.filter(
    (air: FundingApplicationSchemas.ManualAdditionalInformationRequestSchema) => air.status === "in_progress"
  );

  const sortedAirs = [...airs].sort((a, b) => {
    const aActive = a.status === "in_progress";
    const bActive = b.status === "in_progress";
    if (aActive && !bActive) {
      return -1;
    }
    if (!aActive && bActive) {
      return 1;
    }
    const titleCompare = getAirTitle(intl, a).localeCompare(getAirTitle(intl, b));
    if (titleCompare !== 0) {
      return titleCompare;
    }
    return a.public_identifier.localeCompare(b.public_identifier);
  });

  // Returns a fragment so callers can compose us inside their own Flex column alongside other task cards.
  return (
    <>
      {activeManualAirs.map((air) => (
        <TaskCard
          key={air.id}
          taskUniqueId={getManualAirTitleKey(air)}
          isCompleted={false}
          title={getManualAirTitle(intl, air)}
          subtitle={{
            description: getManualAirDescription(intl, air),
            subtext: intl.formatMessage(MANUAL_AIR_MESSAGES.manualAirTimeEstimate),
          }}
          slots={{
            end: (
              <Button variant="Tertiary" onClick={() => setSelectedManualAir(air)}>
                {intl.formatMessage(MANUAL_AIR_MESSAGES.seeDetailsCta)}
              </Button>
            ),
          }}
        />
      ))}
      {sortedAirs.map((air) => {
        const title = getAirTitle(intl, air);
        if (air.status !== "in_progress") {
          const resources = getCompletedAirResources(intl, air);
          return (
            <TaskCard
              key={air.public_identifier}
              taskUniqueId={getAirTitleKey(air)}
              isCompleted={true}
              title={title}
              subtitle={resources.length > 0 ? { resources } : undefined}
              slots={{
                end: <Tag color="positive" content={intl.formatMessage(MESSAGES.completedTag)} />,
              }}
            />
          );
        }
        return (
          <TaskCard
            key={air.public_identifier}
            taskUniqueId={getAirTitleKey(air)}
            isCompleted={false}
            title={title}
            subtitle={{
              description: getAirDescription(intl, air),
              // defaultSubtext doubles as the fallback when an unknown AIR type has no time estimate.
              subtext: getAirTimeEstimate(intl, air) ?? intl.formatMessage(MESSAGES.defaultSubtext),
            }}
            slots={{
              end: (
                <Button variant="Tertiary" onClick={() => navigate(`${airPath}/${air.public_identifier}`)}>
                  {getAirCta(intl, air)}
                </Button>
              ),
            }}
          />
        );
      })}
      <ManualAirDetailsPanel
        air={selectedManualAir}
        open={selectedManualAir !== null}
        onClose={() => setSelectedManualAir(null)}
      />
    </>
  );
};
